<template>
  <!-- ─── Capa do Dossiê — gerada automaticamente ─── -->
  <!-- Este módulo é somente visualização. Não há campos para preencher.
       Os dados vêm do cadastro da empresa e da sessão do usuário. -->
  <div class="dossier-cover">
    <!-- Capa renderizada -->
    <div class="dossier-cover__page" ref="coverPage">
      <!-- Logotipo da instituição -->
      <div class="dossier-cover__logo-area">
        <img
          v-if="institution.logo_url"
          :src="institution.logo_url"
          class="dossier-cover__logo"
          alt="Logotipo da instituição"
        />
        <div v-else class="dossier-cover__logo-placeholder">
          <i class="fas fa-building"></i>
        </div>
      </div>

      <!-- Título principal -->
      <div class="dossier-cover__title-block">
        <h1 class="dossier-cover__main-title">
          DOSSIÊ INSTITUCIONAL DA EMPRESA
        </h1>
        <p class="dossier-cover__main-subtitle">
          Organização documental para fins de gestão e fiscalização
        </p>
      </div>

      <!-- Divisor -->
      <div class="dossier-cover__divider"></div>

      <!-- Dados básicos da empresa -->
      <div class="dossier-cover__data-block">
        <!-- Razão Social + CNPJ na mesma linha -->
        <p class="dossier-cover__data-primary">
          {{ institution.name }}
          <span v-if="institution.cnpj" class="dossier-cover__data-separator"
            >–</span
          >
          <span v-if="institution.cnpj"
            >CNPJ {{ formatCnpj(institution.cnpj) }}</span
          >
        </p>

        <!-- Município / UF -->
        <p class="dossier-cover__data-secondary">
          {{ institution.city }}
          <template v-if="institution.uf"> - {{ institution.uf }}</template>
        </p>

        <!-- Telefone em destaque -->
        <p v-if="institution.phone" class="dossier-cover__data-phone">
          {{ institution.phone }}
        </p>
      </div>

      <!-- Espaço flexível -->
      <div class="dossier-cover__spacer"></div>

      <!-- Rodapé: assinatura eletrônica + data de geração -->
      <div class="dossier-cover__footer">
        <p class="dossier-cover__footer-signature">
          <template v-if="currentUser">
            (Assinatura eletrônica)
            {{ currentUser.name }}
            <template v-if="currentUser.office">
              – {{ currentUser.office }}</template
            >
            <template v-if="currentUser.cpf">
              – CPF: {{ maskCpf(currentUser.cpf) }}</template
            >
          </template>
          – Documento gerado em: {{ generatedAt }}
        </p>
      </div>
    </div>

    <!-- Nota informativa (modo edição — fora da capa impressa) -->
    <div v-if="!isViewMode && !loading" class="dossier-cover__note">
      <i class="fas fa-info-circle mr-1"></i>
      A capa é gerada automaticamente com os dados do cadastro da empresa. Para
      atualizar nome, CNPJ, telefone ou logotipo, acesse as
      <strong>Configurações da Conta</strong>.

      <!-- TODO: quando os campos novos forem adicionados ao cadastro,
           remover este aviso e usar os dados diretamente -->
      <div class="dossier-cover__todo-warn">
        <i class="fas fa-tools mr-1"></i>
        <strong>TODO (alinhamento com Luiz):</strong>
        Os campos <em>Natureza Jurídica</em>,
        <em>Data de início das atividades</em> e
        <em>Capacidade declarada de acolhimento</em> ainda precisam ser
        adicionados ao cadastro da empresa. Quando disponíveis, incluir
        automaticamente na capa.
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import { userData } from "../../lib/functions";

export default {
  name: "DossierCover",

  props: {
    // 'view' = renderização limpa para dossiê/exportação
    // 'edit' = mostra notas e avisos de TODO
    mode: {
      type: String,
      default: "view",
      validator: (v) => ["edit", "view"].includes(v),
    },
  },

  data() {
    return {
      loading: true,
      institution: {
        name: "",
        cnpj: "",
        city: "",
        uf: "",
        phone: "",
        logo_url: null,
        // TODO: adicionar quando disponível no cadastro
        // legal_nature: null,
        // activities_start_date: null,
        // declared_capacity: null,
      },
      currentUser: null,
      generatedAt: "",
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },
  },

  created() {
    this.retrieve();
    this.generatedAt = this.formatDateTime(new Date());
  },

  methods: {
    async retrieve() {
      try {
        this.loading = true;

        // Dados do usuário logado (sessão)
        const { user } = userData();
        this.currentUser = user;

        // Dados da empresa via API
        const response = await api.get("/rest-home/dossier/institution");
        const { error, institution } = response.data;

        if (!error && institution) {
          this.institution = {
            name: institution.name || user?.rest_home_name || "",
            cnpj: institution.cnpj || "",
            city: institution.city || "",
            uf: institution.uf || "",
            phone: institution.phone || "",
            logo_url: institution.logo_url || user?.logo_url || null,
          };
        } else {
          // Fallback para dados da sessão
          this.institution.name = user?.rest_home_name || "";
          this.institution.logo_url = user?.logo_url || null;
        }
      } catch (err) {
        console.error("DossierCover.retrieve:", err);
        // Fallback silencioso — capa ainda renderiza com o que tiver
        const { user } = userData();
        if (user) {
          this.institution.name = user.rest_home_name || "";
          this.institution.logo_url = user.logo_url || null;
        }
      } finally {
        this.loading = false;
      }
    },

    // ── Utilitários ────────────────────────────────────────────────────────

    formatCnpj(cnpj) {
      if (!cnpj) return "";
      const digits = String(cnpj).replace(/\D/g, "");
      if (digits.length !== 14) return cnpj;
      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    maskCpf(cpf) {
      if (!cpf) return "";
      const digits = String(cpf).replace(/\D/g, "");
      if (digits.length !== 11) return cpf;
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    },

    formatDateTime(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${pad(date.getDate())}/${pad(
          date.getMonth() + 1
        )}/${date.getFullYear()} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
          date.getSeconds()
        )}`
      );
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER CAPA
   Página A4 simulada para visualização e impressão.
   Em modo de exportação PDF, o componente pai usa html2pdf ou
   endpoint de backend para capturar o conteúdo de .dossier-cover__page.
   ═══════════════════════════════════════════════════════════════ */

.dossier-cover {
  width: 100%;
}

/* ── Página simulada (proporção A4) ──────────────────────────── */
.dossier-cover__page {
  width: 100%;
  max-width: 794px; /* largura A4 em 96dpi */
  min-height: 500px;
  margin: 0 auto;
  padding: 48px 56px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  /* Fonte padrão da capa */
  font-family: "WorkSans", Arial, sans-serif;
  color: #222;
}

/* ── Logotipo ────────────────────────────────────────────────── */
.dossier-cover__logo-area {
  margin-bottom: 32px;
}

.dossier-cover__logo {
  max-height: 90px;
  max-width: 280px;
  object-fit: contain;
}

.dossier-cover__logo-placeholder {
  width: 90px;
  height: 90px;
  background: #f0f0ef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #bbb;
}

/* ── Título principal ────────────────────────────────────────── */
.dossier-cover__title-block {
  text-align: center;
  margin-bottom: 24px;
}

.dossier-cover__main-title {
  font-size: 22px;
  font-weight: 700;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 8px;
  line-height: 1.3;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
}

.dossier-cover__main-subtitle {
  font-size: 12px;
  color: #555;
  margin: 0;
  font-style: italic;
}

/* ── Divisor ─────────────────────────────────────────────────── */
.dossier-cover__divider {
  width: 60%;
  height: 2px;
  background: linear-gradient(to right, transparent, #1f5a76, transparent);
  margin: 0 auto 32px;
}

/* ── Bloco de dados ──────────────────────────────────────────── */
.dossier-cover__data-block {
  text-align: center;
  margin-bottom: 16px;
}

.dossier-cover__data-primary {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  margin: 0 0 6px;
  line-height: 1.5;
}

.dossier-cover__data-separator {
  margin: 0 6px;
  color: #aaa;
}

.dossier-cover__data-secondary {
  font-size: 12px;
  color: #555;
  margin: 0 0 8px;
}

.dossier-cover__data-phone {
  font-size: 26px;
  font-weight: 700;
  color: #1f5a76;
  margin: 0;
  font-family: "WorkSans-SemiBold", Arial, sans-serif;
  letter-spacing: 1px;
}

/* ── Espaço flexível ─────────────────────────────────────────── */
.dossier-cover__spacer {
  flex: 1;
  min-height: 40px;
}

/* ── Rodapé ──────────────────────────────────────────────────── */
.dossier-cover__footer {
  width: 100%;
  border-top: 1px solid #e0e0e0;
  padding-top: 10px;
  text-align: center;
}

.dossier-cover__footer-signature {
  font-size: 10px;
  color: #888;
  margin: 0;
  line-height: 1.6;
}

/* ── Loading ─────────────────────────────────────────────────── */
.dossier-cover__loading {
  padding: 32px;
  text-align: center;
  color: #888;
  font-size: 13px;
}

/* ── Nota informativa (fora da capa impressa) ────────────────── */
.dossier-cover__note {
  max-width: 794px;
  margin: 12px auto 0;
  font-size: 13px;
  color: #555;
  background: #f8f8f7;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 10px 14px;
  line-height: 1.6;
}

/* ── Aviso de TODO ───────────────────────────────────────────── */
.dossier-cover__todo-warn {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 12px;
  color: #856404;
  line-height: 1.6;
}

/* ── Impressão ───────────────────────────────────────────────── */
@media print {
  .dossier-cover__note {
    display: none;
  }
  .dossier-cover__page {
    box-shadow: none;
    border: none;
    padding: 48px 56px;
  }
}
</style>